import { isDeliveryEmail, normalizeDeliveryEmail } from "@lc/shared/email-address";
import type { PostSubmitContext } from "@/types/config";

// Parse + validate the post-submit redirect query (`?email=&p=lm-<slug>&k=<kind>`)
// into a safe context. Shared by the server (+onBeforeRender) and the client
// (LeadMagnetNextSteps) so both apply the SAME allow-listing.
//
// Security: these values are attacker-controllable (anyone can craft the URL).
// React escapes rendered text so there is no XSS, and none of these flow into an
// href/redirect/eval — but we still hard-validate to prevent (a) arbitrary
// reflected text in the "мы отправили на <…>" line (social engineering), (b)
// analytics pollution via the tracked source, and (c) junk attribute/lookup
// values. Anything that fails validation degrades to empty (→ generic copy).

const SLUG_RE = /^[a-z0-9-]+$/;
const KIND_RE = /^[a-z-]+$/;
const CONNECT_TOKEN_RE = /^[A-Za-z0-9_-]{8,64}$/;

const MAX_SLUG = 64;
const MAX_KIND = 32;

export interface RawPostSubmit {
  email?: string | null;
  p?: string | null;
  k?: string | null;
  t?: string | null;
}

export function sanitizePostSubmit(raw: RawPostSubmit): PostSubmitContext {
  const emailRaw = normalizeDeliveryEmail(raw.email ?? "");
  const email = isDeliveryEmail(emailRaw) ? emailRaw : "";

  const pRaw = (raw.p ?? "").trim().toLowerCase();
  const sourceRaw = pRaw.startsWith("lm-") ? pRaw.slice(3) : pRaw;
  const source =
    sourceRaw.length > 0 && sourceRaw.length <= MAX_SLUG && SLUG_RE.test(sourceRaw)
      ? sourceRaw
      : "";

  const kindRaw = (raw.k ?? "").trim().toLowerCase();
  const kind =
    kindRaw.length > 0 && kindRaw.length <= MAX_KIND && KIND_RE.test(kindRaw) ? kindRaw : "";

  const connectTokenAvailable = CONNECT_TOKEN_RE.test((raw.t ?? "").trim());

  return { email, source, kind, connectTokenAvailable };
}
